import { Button } from "components/DesignSystem/Button/Button";
import ChatCard from "components/DesignSystem/ChatCard/ChatCard";
import Modal from "components/DesignSystem/Modal/Modal";
import { Tag } from "components/DesignSystem/Tag/Tag";
import ToolTip from "components/design/toolTip";
import { UserTrustIcon } from "components/icons/JouneyTooltip/UserTrustIcon";
import { BareInput, Label } from "components/DesignSystem/TextInput/TextInput";
import { Banner } from "components/DesignSystem/Banner/Banner";
import { ShieldCheck } from "components/icons/ShieldCheck";
import { REQUESTED, SUBMITTED } from "constants/sensitiveData";
import { TableUI } from "components/design/TableUI";
import { useModal } from "hooks/useModal";
import { useToast } from "hooks/useToast";
import { useMemo, useState } from "react";
import BankCircular from "static/images/BankCircular.svg";
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from "react-table-8.10.7";
import { useRespondToAccountantAccessRequestMutation } from "store/apis/bankConnections";
import { BackendError } from "types/utils/error";
import { BalancePaymentHeader } from "components/icons/BalancePaymentHeader";
import { Divider } from "components/design/Divider";
import { FilledGreenWiteTick } from "components/icons/FilledGreenWhiteTick";
import { GrantAccountantAccessModal } from "components/OnboardingWidget/Tax/GrantAccountantAccessModal";
import { Undo } from "components/icons/Undo";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";

type EntityItem = {
  entity_item_id: string;
  name?: string;
  logo?: string;
  bank_name?: string;
  bank_logo?: string;
  responded?: boolean;
  entity_name?: string;
  accountant_access?: boolean | null;
  accountant_access_reason?: string;
};

type AccountantAccessCardCustomData = {
  card_status: string;
  header: string;
  metadata: {
    accountant_access_request_id?: string;
    entity_id: string;
    entity_name?: string;
    entity_items: EntityItem[];
  };
};

const CONFIRMATION_PHRASE =
  "I have logged into my data source and granted accountant access";

const columnHelper = createColumnHelper<EntityItem>();

const buildEntityItemResponse = (item: EntityItem) => ({
  entity_item_id: item.entity_item_id,
  accountant_access: true,
  reason: "",
});

const ConfirmationModal = ({
  isOpen,
  close,
  onConfirm,
  isLoading,
}: {
  isOpen: boolean;
  close: () => void;
  onConfirm: () => void;
  isLoading: boolean;
}) => {
  const [inputValue, setInputValue] = useState("");
  const isMatched = inputValue.trim() === CONFIRMATION_PHRASE;

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setInputValue("");
      close();
    }
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={handleOpenChange}>
      <Modal.Content>
        <Modal.Header>
          <div className="t-flex t-items-center t-gap-2">
            <span className="t-text-orange">
              <ShieldCheck size="20" />
            </span>
            <Modal.Title>Confirm Bank Access Granted</Modal.Title>
          </div>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <div className="t-flex t-flex-col t-gap-5">
            <Banner
              type="warning"
              title="Please verify before submitting"
              description="By submitting, you confirm that you have personally logged into your data source and granted the accountant access to Inkle's team. We cannot verify this on our end — we rely entirely on your confirmation to proceed with your books."
            />
            <div className="t-flex t-flex-col t-gap-3">
              <p className="t-text-body-sm t-text-text-60 t-m-0">
                To confirm, type{" "}
                <strong className="t-text-text-100 t-italic">
                  &ldquo;{CONFIRMATION_PHRASE}&rdquo;
                </strong>{" "}
                in the box below:
              </p>
              <div>
                <Label>Confirmation phrase</Label>
                <BareInput
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder="Type the phrase above exactly"
                  customType={inputValue && !isMatched ? "error" : undefined}
                />
                {inputValue && !isMatched && (
                  <p className="t-text-body-sm t-text-red-50 t-m-0 t-mt-1">
                    Phrase does not match. Please type it exactly as shown.
                  </p>
                )}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Modal.RawClose asChild>
            <Button type="button">Cancel</Button>
          </Modal.RawClose>
          <Button
            customType="primary"
            onClick={onConfirm}
            disabled={!isMatched || isLoading}
            isLoading={isLoading}
          >
            Submit Response
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};

const AccessCell = ({
  item,
  isGranted,
  isUndoable,
  onGrant,
  onUndo,
}: {
  item: EntityItem;
  isGranted: boolean;
  isUndoable: boolean;
  onGrant: () => void;
  onUndo: () => void;
}) => {
  const grantAccessModal = useModal();
  const isSmallScreen = useIsSmallScreen();
  const buttonSize = isSmallScreen ? "xsmall" : "small";

  const handleConfirmAccessGranted = () => {
    onGrant();
    grantAccessModal.close();
  };

  if (isGranted) {
    return (
      <div className="t-flex t-items-center t-justify-end t-gap-2 t-w-full">
        <Tag tagType="green" icon={false}>
          Access granted
        </Tag>
        {isUndoable && (
          <ToolTip text="Undo">
            <span>
              <Button customType="ghost_icon" size="small" onClick={onUndo}>
                <Undo />
              </Button>
            </span>
          </ToolTip>
        )}
      </div>
    );
  }

  return (
    <div className="t-flex t-items-center t-justify-end t-gap-2 t-w-full">
      <Button
        customType="primary_outlined"
        size={buttonSize}
        onClick={grantAccessModal.open}
      >
        Grant access
      </Button>
      <ToolTip text="Mark as access granted already">
        <span>
          <Button customType="icon" size={buttonSize} onClick={onGrant}>
            <UserTrustIcon />
          </Button>
        </span>
      </ToolTip>
      <GrantAccountantAccessModal
        open={grantAccessModal.isOpen}
        bankName={item.name || item.bank_name || ""}
        isLoading={false}
        onCancel={grantAccessModal.close}
        onConfirm={handleConfirmAccessGranted}
      />
    </div>
  );
};

const RespondModal = ({
  isOpen,
  close,
  entityId,
  requestId,
  messageId,
  entityItems,
  customData,
}: {
  isOpen: boolean;
  close: () => void;
  entityId: string;
  requestId: string;
  messageId: string;
  entityItems: EntityItem[];
  customData: AccountantAccessCardCustomData;
}) => {
  const { alertToast, successToast } = useToast();
  const [grantedEntityItemIds, setGrantedEntityItemIds] = useState<
    Record<string, boolean>
  >({});
  const [respondToRequest, { isLoading }] =
    useRespondToAccountantAccessRequestMutation();
  const confirmationModal = useModal();
  const footerButtonClassName = "sm:t-flex-1";

  const handleAccessGranted = (entityItemId: string) => {
    setGrantedEntityItemIds((prev) => ({ ...prev, [entityItemId]: true }));
  };

  const handleUndoAccessGranted = (entityItemId: string) => {
    setGrantedEntityItemIds((prev) => {
      const next = { ...prev };
      delete next[entityItemId];
      return next;
    });
  };

  const willSubmitYes = entityItems.some((item) => {
    if (grantedEntityItemIds[item.entity_item_id]) return true;
    return item.responded && item.accountant_access === true;
  });

  const handleAPICall = async () => {
    try {
      await respondToRequest({
        entityId,
        messageCardId: requestId,
        entityItemResponses: entityItems
          .filter((item) => grantedEntityItemIds[item.entity_item_id])
          .map((item) => buildEntityItemResponse(item)),
      }).unwrap();

      successToast({ message: "Response submitted successfully" });
      confirmationModal.close();
      close();
    } catch (e) {
      alertToast({ message: (e as BackendError).data?.error?.message });
    }
  };

  const handleSubmit = () => {
    if (willSubmitYes) {
      confirmationModal.open();
      return;
    }
    handleAPICall();
  };

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        header: "Bank Account",
        size: 50,
        cell: ({ row }) => (
          <div className="t-flex t-items-center t-gap-3">
            <img
              src={row.original.logo || row.original.bank_logo || BankCircular}
              className="t-w-8 t-h-8 t-shrink-0 t-rounded-full sm:t-w-6 sm:t-h-6"
              alt={row.original.name || row.original.bank_name}
            />
            <span className="t-text-body-sm t-text-text-100">
              {row.original.name || row.original.bank_name}
            </span>
          </div>
        ),
      }),
      columnHelper.display({
        id: "access",
        header: () => (
          <div className="t-text-right t-w-full">
            <span className="sm:t-hidden">Shared accountant access?</span>
            <span className="t-hidden sm:t-inline">Access</span>
          </div>
        ),
        size: 50,
        cell: ({ row }) => {
          const item = row.original;
          const isLocallyGranted = Boolean(
            grantedEntityItemIds[item.entity_item_id]
          );
          const isGranted =
            isLocallyGranted ||
            (item.responded && item.accountant_access === true);

          return (
            <AccessCell
              item={item}
              isGranted={Boolean(isGranted)}
              isUndoable={isLocallyGranted}
              onGrant={() => handleAccessGranted(item.entity_item_id)}
              onUndo={() => handleUndoAccessGranted(item.entity_item_id)}
            />
          );
        },
      }),
    ],
    [grantedEntityItemIds]
  );

  const table = useReactTable({
    data: entityItems,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      <Modal.Root open={isOpen} onOpenChange={close}>
        <Modal.Content>
          <Modal.Header className="sm:t-pl-4">
            <Modal.Title>Accountant Access Requested</Modal.Title>
            <Modal.Close />
          </Modal.Header>
          <Modal.Body className="sm:t-px-4">
            <p className="t-text-body-sm t-text-text-30 t-mb-4">
              Providing accountant access allows our bookkeeping team to
              securely retrieve full bank statements for reconciliation, ensure
              accurate bookkeeping, and assist you with any accounting queries
              or requirements.
            </p>
            <TableUI table={table} verticalAlign="top" />
          </Modal.Body>
          <Modal.FooterButtonGroup>
            <Modal.RawClose asChild>
              <Button type="button" className={footerButtonClassName}>
                Close
              </Button>
            </Modal.RawClose>
            <Button
              customType="primary"
              onClick={handleSubmit}
              className={footerButtonClassName}
              disabled={
                Object.keys(grantedEntityItemIds).length === 0 || isLoading
              }
              isLoading={isLoading}
            >
              Submit
            </Button>
          </Modal.FooterButtonGroup>
        </Modal.Content>
      </Modal.Root>
      {confirmationModal.isOpen && (
        <ConfirmationModal
          isOpen={confirmationModal.isOpen}
          close={confirmationModal.close}
          onConfirm={handleAPICall}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export const AccountantAccessChatCard = ({
  customData,
  size,
  messageId,
}: {
  customData: AccountantAccessCardCustomData;
  size?: "regular" | "block";
  messageId: string;
}) => {
  const { card_status, metadata } = customData || {};
  const { entity_id, entity_items = [] } = metadata || {};
  const respondModal = useModal();

  const isRequested = card_status === REQUESTED;
  const isSubmitted = card_status === SUBMITTED;

  const requestId = messageId;

  const allResponded =
    entity_items.length > 0 && entity_items.every((item) => item.responded);

  return (
    <>
      <ChatCard.Root size={size}>
        <ChatCard.Header>
          <div className="t-flex t-gap-2 t-items-center">
            <span className="t-inline-flex t-flex-shrink-0">
              <BalancePaymentHeader />
            </span>
            <span>
              {isSubmitted
                ? "Accountant Access Updated"
                : "Accountant Access Requested"}
            </span>
          </div>
        </ChatCard.Header>
        <ChatCard.Body>
          <div className="t-flex t-flex-col t-gap-2">
            {entity_items.map((item) => (
              <div
                key={item.entity_item_id}
                className="t-flex t-items-center t-gap-4"
              >
                {item.responded ? (
                  <span className="t-flex-shrink-0">
                    <FilledGreenWiteTick size="9" />
                  </span>
                ) : (
                  <span className="t-w-2 t-h-2 t-rounded-full t-bg-yellow-70 t-inline-block t-flex-shrink-0" />
                )}
                <img
                  src={item.logo || item.bank_logo || BankCircular}
                  className="t-w-6 t-h-6 t-rounded-full t-flex-shrink-0 t-object-cover"
                  alt={item.name || item.bank_name}
                />
                <div className="t-min-w-0">
                  <div className="t-text-subtext-sm t-text-text-60 t-truncate">
                    {item.name || item.bank_name}
                  </div>
                  {item.entity_name && (
                    <div className="t-text-body-sm t-text-text-30 t-truncate">
                      {item.entity_name}
                    </div>
                  )}
                </div>
              </div>
            ))}
            <div className="t-text-body-sm t-mt-2">
              <Divider type="dotted" />
              <div className="t-text-body-sm t-mt-3">
                Follow the steps provided in{" "}
                <a
                  href="https://www.inkle.ai/help/giving-bank-access-to-team-inkle-as-bookkeeper"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="t-text-purple"
                >
                  this link
                </a>{" "}
                to grant us accountant access, & then update here once done.
              </div>
            </div>
          </div>
          {(isRequested || allResponded || isSubmitted) && (
            <Button
              customType={allResponded ? "secondary" : "primary"}
              block
              size="small"
              onClick={respondModal.open}
            >
              {allResponded ? "View" : "View request"}
            </Button>
          )}
        </ChatCard.Body>
      </ChatCard.Root>
      {respondModal.isOpen && (
        <RespondModal
          {...respondModal}
          entityId={entity_id}
          requestId={requestId}
          messageId={messageId}
          entityItems={entity_items}
          customData={customData}
        />
      )}
    </>
  );
};
